// =================================================
// IMPORT
// -------------------------------------------------
// Dependencies
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { DateTime, Duration } from "luxon";
import { Helmet } from "react-helmet";
// -------------------------------------------------
// Contexts
import { useAuth } from "../contexts/auth";
// -------------------------------------------------
// Components
import AppRoot from "../components/App_Root";
// -------------------------------------------------
// Redux
import { fetchStudyList } from "../redux/reducers/studies";
import { surveysSelectors } from "../redux/reducers/surveys";
import { taskResponsesSelectors } from "../redux/reducers/taskResponses";
import { ticketsSelectors } from "../redux/reducers/tickets";
import {
  fetchConsumerList,
  consumersSelectors,
} from "../redux/reducers/consumers";
// -------------------------------------------------
// Components
import DashboardCBTIContent from "../components/DashboardCBTI_Content";
import DashboardCBTIOverview from "../components/DashboardCBTI_Overview";
// -------------------------------------------------
// Basic elements
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
// -------------------------------------------------
// Icons
import { unique } from "../supportFunc/unique";
// =================================================
// FUNCTIONAL COMPONENT
const DashboardCBTI = () => {
  const { t } = useTranslation("pages", { keyPrefix: "DashboardCBTI" });
  // ===============================================
  // VARIABLES
  // -----------------------------------------------
  // Contexts
  const { currentAuth } = useAuth();
  // -----------------------------------------------
  // Local state
  const [currentConsumerId, setCurrentConsumerId] = useState(false);
  const [resultsByUser, setResultsByUser] = useState({});
  // -----------------------------------------------
  // Redux
  const dispatch = useDispatch();
  const currentUserId = useSelector(
    (state) => state.user.currentUser && state.user.currentUser._id,
  );
  const currentRole = useSelector(
    (state) => state.user.currentUser && state.user.currentUser.primaryRole,
  );
  const studiesStatus = useSelector((state) => state.studies.status);
  const consumersStatus = useSelector((state) => state.consumers.status);
  const consumerEntities = useSelector((state) =>
    consumersSelectors.selectEntities(state),
  );
  const surveyEntities = useSelector((state) => state.surveys.entities);
  const surveyList = useSelector((state) => surveysSelectors.selectAll(state));
  const ticketList = useSelector((state) => ticketsSelectors.selectAll(state));
  const taskResponseList = useSelector((state) =>
    taskResponsesSelectors.selectAll(state),
  );
  // -----------------------------------------------
  // Browser URL location
  const location = useLocation();
  const navigate = useNavigate();
  // ===============================================
  // FUNCTIONS
  // USE EFFECTS
  // -----------------------------------------------
  // Upon render, get the studies list and set the redux state
  useEffect(() => {
    // Init
    let enrolledStudyId;
    // Fetch all studies from DB if not done yet
    if (studiesStatus === "idle" && currentRole !== "participant") {
      dispatch(fetchStudyList({ requestingUser: currentAuth }));
    }
    // Fetch all consumers from DB if not done yet
    if (consumersStatus === "idle" && currentRole !== "participant") {
      dispatch(fetchConsumerList({ requestingUser: currentAuth }));
    }
    // Get the study-id from URL and get the current study
    if (
      consumersStatus === "succeeded" ||
      (currentRole === "participant" && consumersStatus === "idle")
    ) {
      const searchParams = new URLSearchParams(location.search);
      const studyId = searchParams.get("studyId");
      const consumerId = searchParams.get("consumerId");
      if (consumerId) {
        // First check that the study ID in the URL is the same as the enrolled study
        enrolledStudyId =
          consumerEntities[consumerId] &&
          consumerEntities[consumerId].studyEnrollmentList.length > 0 &&
          consumerEntities[consumerId].studyEnrollmentList[0].studyId;
        if (enrolledStudyId && enrolledStudyId !== studyId) {
          navigate(
            `/dashboard-cbti?studyId=${enrolledStudyId}&consumerId=${consumerId}`,
            { replace: true },
          );
          return;
        }
        // Ok study id is the same as the enrolled study
        setCurrentConsumerId(consumerId);
        computeResults(consumerId);
      } else {
        // If a participant is signed in, then automatically navigate to their dashboard
        if (currentRole === "participant") {
          navigate(`/dashboard-cbti?consumerId=${currentUserId}`, {
            replace: true,
          });
        } else {
          setCurrentConsumerId(null);
          computeResults(true); // compute for all users
        }
      }
    }
  }, [studiesStatus, consumersStatus, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to calculate mean
  function calculateMean(arr) {
    const sum = arr.reduce((acc, num) => acc + num, 0); // Sum all elements
    return sum / arr.length; // Divide sum by number of elements
  }

  // Function to calculate median
  function calculateMedian(arr) {
    const sorted = arr.slice().sort((a, b) => a - b); // Sort ascending
    const mid = Math.floor(sorted.length / 2);

    return sorted.length % 2 !== 0
      ? sorted[mid] // Odd length → middle element
      : (sorted[mid - 1] + sorted[mid]) / 2; // Even length → average of two middle elements
  }

  // Compute weekly average scores for a given data key
  const computeWeeklyScore = (responses, dataKey, cbtiDates) => {
    if (!responses) {
      return {};
    }
    // Sort responses by date and group them by the week of the CBTI modules
    const dataEntries = {};
    responses
      .sort((a, b) => a.dateAvailable.localeCompare(b.dateAvailable))
      .forEach((response) => {
        if (response && response.data && response.data[dataKey]) {
          let value;
          if (
            typeof response.data[dataKey] === "string" &&
            response.data[dataKey].startsWith("PT")
          ) {
            const duration = Duration.fromISO(response.data[dataKey]);
            value = duration.as("minutes");
          } else if (dataKey === "CSD11") {
            // Sleep quality is a 0-4 scale, so we need to add 1 to the value
            value = parseInt(response.data[dataKey]) + 1;
          } else {
            value = parseFloat(response.data[dataKey]);
          }
          // Assign value to pre-CBTI, module #, or post-CBTI
          if (!isNaN(value)) {
            const isLaterDate = cbtiDates.map((isodate) => {
              return (
                DateTime.fromISO(response.dateAvailable)
                  .startOf("day")
                  .diff(DateTime.fromISO(isodate).startOf("day"))
                  .toMillis() <
                24 * 60 * 60 * 1000
              );
            });
            const lastTrueIndex = isLaterDate.lastIndexOf(false) + 1;
            if (!dataEntries[lastTrueIndex]) {
              dataEntries[lastTrueIndex] = {};
              dataEntries[lastTrueIndex].x = [];
              dataEntries[lastTrueIndex].y = [];
            }
            dataEntries[lastTrueIndex].x.push(response.dateAvailable);
            dataEntries[lastTrueIndex].y.push(value);
          }
        }
      });
    // Compute the median for each phase (pre-CBTI, module #, and post-CBTI)
    const dataWeekAvg = {};
    for (const key in dataEntries) {
      dataWeekAvg[key] = {};
      dataWeekAvg[key].x = [
        DateTime.fromISO(dataEntries[key].x[0])
          .toLocal()
          .minus({ day: 1 })
          .toMillis(),
        DateTime.fromISO(dataEntries[key].x[dataEntries[key].x.length - 1])
          .toLocal()
          .toMillis(),
      ];
      dataWeekAvg[key].mu = calculateMean(dataEntries[key].y);
      dataWeekAvg[key].md = calculateMedian(dataEntries[key].y);
    }
    return { dataEntries, dataWeekAvg };
  };

  function computeResultsForUser(
    userId,
    sleepLogTaskResponses,
    cbtiTaskResponses,
  ) {
    const sleepLogSurveyIds = surveyList
      .filter((survey) => survey.acronym.startsWith("CSD"))
      .map((survey) => survey._id);
    const cbtiSurveyIds = surveyList
      .filter((survey) => survey.acronym.startsWith("CBCTI"))
      .map((survey) => survey._id);
    const totalSleepTime = computeWeeklyScore(
      sleepLogTaskResponses.filter((response) => response.userId === userId),
      "CSD_TOTAL_SLEEP_TIME",
      cbtiTaskResponses
        .filter((response) => response.userId === userId)
        .map((response) => response.dateAvailable),
    );
    const sleepEfficiency = computeWeeklyScore(
      sleepLogTaskResponses.filter((response) => response.userId === userId),
      "CSD_SLP_EFF_TST_TIB",
      cbtiTaskResponses
        .filter((response) => response.userId === userId)
        .map((response) => response.dateAvailable),
    );
    const sleepOnsetLatency = computeWeeklyScore(
      sleepLogTaskResponses.filter((response) => response.userId === userId),
      "CSD5",
      cbtiTaskResponses
        .filter((response) => response.userId === userId)
        .map((response) => response.dateAvailable),
    );
    const wakeAfterSleepOnset = computeWeeklyScore(
      sleepLogTaskResponses.filter((response) => response.userId === userId),
      "CSD7",
      cbtiTaskResponses
        .filter((response) => response.userId === userId)
        .map((response) => response.dateAvailable),
    );
    const sleepQuality = computeWeeklyScore(
      sleepLogTaskResponses.filter((response) => response.userId === userId),
      "CSD11",
      cbtiTaskResponses
        .filter((response) => response.userId === userId)
        .map((response) => response.dateAvailable),
    );
    const results = {
      userNumber:
        consumerEntities[userId] &&
        consumerEntities[userId].studyEnrollmentList[0]
          ? consumerEntities[userId].studyEnrollmentList[0].userStudyNumber
          : consumerEntities[userId]
            ? consumerEntities[userId].userNumber
            : "n/a",
      email: consumerEntities[userId]?.email || "no-email",
      totalSleepTime: totalSleepTime.dataWeekAvg,
      sleepEfficiency: sleepEfficiency.dataWeekAvg,
      sleepOnsetLatency: sleepOnsetLatency.dataWeekAvg,
      wakeAfterSleepOnset: wakeAfterSleepOnset.dataWeekAvg,
      sleepQuality: sleepQuality.dataWeekAvg,
      sleepLogTaskResponses: sleepLogTaskResponses
        .filter((response) => response.userId === userId)
        .sort((a, b) => a.dateAvailable.localeCompare(b.dateAvailable)),
      cbtiTaskResponses: cbtiTaskResponses
        .filter((response) => response.userId === userId)
        .sort((a, b) => a.dateAvailable.localeCompare(b.dateAvailable)),
      sleepLogTickets: ticketList
        .filter(
          (ticket) =>
            ticket.userId === userId &&
            sleepLogSurveyIds.includes(ticket.surveyId),
        )
        .sort((a, b) => a.dateAvailable.localeCompare(b.dateAvailable)),
      cbtiTickets: ticketList
        .filter(
          (ticket) =>
            ticket.userId === userId && cbtiSurveyIds.includes(ticket.surveyId),
        )
        .map((ticket) => {
          return {
            ...ticket,
            acronym: surveyEntities[ticket.surveyId].acronym,
          };
        })
        .sort((a, b) => a.dateAvailable.localeCompare(b.dateAvailable)),
    };
    return results;
  }

  const computeResults = (uId) => {
    // Get all the task response data and extract those of CSD and CBCTI surveys
    const sleepLogTaskResponses = taskResponseList.filter(
      (response) =>
        (uId === true || response.userId === uId) &&
        Object.keys(response.data).some((alias) => alias.startsWith("CSD")),
    );
    const cbtiTaskResponses = taskResponseList.filter(
      (response) =>
        (uId === true || response.userId === uId) &&
        Object.keys(response.data).some((alias) => alias.startsWith("CBCTI")),
    );
    // Create a list of unique user IDs from the task responses
    // and filter them to only include those present in the consumerEntities (supervised by the current investigator)
    const userIdList = unique([
      ...sleepLogTaskResponses.map((response) => response.userId),
      ...cbtiTaskResponses.map((response) => response.userId),
    ]).filter(
      (userId) =>
        currentUserId === userId ||
        Object.keys(consumerEntities).includes(userId),
    );
    let results = {};
    userIdList.forEach((userId) => {
      results[userId] = computeResultsForUser(
        userId,
        sleepLogTaskResponses,
        cbtiTaskResponses,
      );
    });
    // Compute the weekly scores for each task response
    setResultsByUser(results);
  };
  // ===============================================
  // RENDER COMPONENT
  return (
    <AppRoot>
      <Helmet>
        <title>
          {process.env.REACT_APP_TITLE} | {t("Sleep overview")}
        </title>
      </Helmet>
      <Toolbar />
      <Grid container className="w-100" wrap="nowrap">
        <Grid item xs={12}>
          {currentConsumerId ? (
            <DashboardCBTIContent
              currentRole={currentRole}
              participantData={Object.values(resultsByUser)[0]}
            />
          ) : (
            <DashboardCBTIOverview resultsByUser={resultsByUser} />
          )}
        </Grid>
      </Grid>
    </AppRoot>
  );
};
// =================================================
// EXPORT COMPONENT
export default DashboardCBTI;
